import { useState } from "react";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Brain,
  CreditCard,
  Banknote,
  Calculator,
  ShoppingCart,
  Loader2,
  FileText,
  Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const analysisTypes = [
  { id: "credit_report", label: "Credit Report", icon: CreditCard, 
    placeholder: "Paste your credit report details here...\n\nExample:\n- Credit Score: 720\n- Open accounts: 3 credit cards, 1 auto loan\n- Total credit limit: £15,000\n- Current utilization: 25%\n- Missed payments: None in last 6 years\n- Hard inquiries: 2 in last 12 months" },
  { id: "bank_statement", label: "Bank Statement", icon: Banknote,
    placeholder: "Paste your bank statement transactions here...\n\nExample monthly summary:\n- Salary deposit: £3,500\n- Rent: £1,200\n- Utilities: £150\n- Groceries: £400\n- Transport: £200\n- Subscriptions: £80\n- Dining out: £150" },
  { id: "budget_planner", label: "Budget Planner", icon: Calculator,
    placeholder: "Enter your financial details for budget planning...\n\nMonthly Income: £3,500\nFixed Expenses:\n- Rent/Mortgage: £1,200\n- Insurance: £100\n- Utilities: £150\n- Phone: £50\n\nVariable Expenses:\n- Food: £400\n- Transport: £200\n- Entertainment: £150" },
  { id: "product_recommendation", label: "Product Recommendation", icon: ShoppingCart,
    placeholder: "Describe your situation for mortgage product recommendations...\n\nExample:\n- First-time buyer\n- Property value: £300,000\n- Deposit: £30,000 (10%)\n- Annual income: £55,000\n- Employment: Employed, 3 years\n- Credit score: Good (720+)\n- Looking for: Best rate, fixed term" }
];

export default function AIAnalysis() {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState("credit_report");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const runAnalysis = async () => {
    if (!content.trim()) {
      toast.error("Please enter content to analyze");
      return;
    }
    setLoading(true);
    setResult("");
    try {
      const response = await axios.post(`${API}/ai/analyze`, {
        content,
        analysis_type: activeTab
      }, { headers: getAuthHeaders() });
      setResult(response.data.analysis);
      toast.success("Analysis complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    }
    setLoading(false);
  };

  const activeType = analysisTypes.find(t => t.id === activeTab);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="ai-analysis">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-500" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold">AI Financial Analysis</h1>
          </div>
          <p className="text-slate-400">Leverage AI to analyze your financial data and get personalized insights.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <Card className="bg-[#0f172a] border-slate-800">
            <CardHeader>
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setResult(""); }}>
                <TabsList className="bg-[#020617] grid grid-cols-2 lg:grid-cols-4">
                  {analysisTypes.map(type => (
                    <TabsTrigger 
                      key={type.id} 
                      value={type.id}
                      className="data-[state=active]:bg-blue-600 text-xs"
                      data-testid={`tab-${type.id}`}
                    >
                      <type.icon className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">{type.label}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{activeType?.label} Input</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setContent(activeType?.placeholder || "")}
                    className="text-xs text-slate-400"
                  >
                    Load Example
                  </Button>
                </div>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={activeType?.placeholder}
                  className="bg-[#020617] border-slate-700 min-h-[300px] font-mono text-sm"
                  data-testid="analysis-input"
                />
              </div>
              <Button
                onClick={runAnalysis}
                disabled={loading || !content.trim()}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                data-testid="run-analysis-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Run AI Analysis
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card className="bg-[#0f172a] border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Analysis Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-blue-500/20 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="mt-4 text-slate-400">AI is analyzing your data...</p>
                  <p className="text-xs text-slate-500 mt-1">This may take a moment</p>
                </div>
              ) : result ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="prose prose-invert prose-sm max-w-none"
                >
                  <div className="bg-[#020617] rounded-lg p-4 border border-slate-800 overflow-auto max-h-[500px]">
                    <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans">
                      {result}
                    </pre>
                  </div>
                </motion.div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Brain className="w-16 h-16 text-slate-700 mb-4" />
                  <p className="text-slate-400">Enter your financial data and click "Run AI Analysis"</p>
                  <p className="text-xs text-slate-500 mt-2">Your analysis results will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {analysisTypes.map((type, index) => (
            <motion.div
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`bg-[#0f172a] border-slate-800 cursor-pointer transition-all hover:border-blue-500/50 ${
                activeTab === type.id ? 'border-blue-500 ring-1 ring-blue-500/20' : ''
              }`} onClick={() => setActiveTab(type.id)}>
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
                    <type.icon className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{type.label}</h3>
                  <p className="text-xs text-slate-400">
                    {type.id === "credit_report" && "Analyze credit history and get improvement tips"}
                    {type.id === "bank_statement" && "Review transactions and spending patterns"}
                    {type.id === "budget_planner" && "Create a personalized budget plan"}
                    {type.id === "product_recommendation" && "Get matched with suitable mortgage products"}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
